/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import axiosWrapper from "@AxiosWrapper";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import MailContent from "./MailContent";
import AttachmentsModal from "./AddUsers/AttachmentsModal";
import { FileManagerAsPopup } from "@Features/files";
import { formCategories } from "@Helpers";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { Avatar } from "@mui/material";
import SplitButton from "@CubeComponents/html/SplitButtonComponent";
import AddUsersToMailList from "./AddUsers/AddUsersToMailList";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { getBackendUrl } from "@Utils";
import { sanitizeHtml } from "@Utils/sanitize";
import { uploadStringToTemp } from "@Services/tempUpload";
import IconComponent from "@CubeComponents/icons";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { useFormValidator, TEMPLATE_SOURCE_MAIL_CREATION } from "@Features/forms";
import { setProjectData } from "@Reducers/breadcrumbsdata";
import Text from "@CubeComponents/html/Text/Text";
import {
  addProjectWhitelistEmailsRequest,
  inheritMailCustomFields,
  sendMailRequest,
} from "@Features/mailbox/api/mailbox.api";
import type { Attachment, Reference, FormTemplate, MailItem, Recipient } from "@ServerTypes/mails/mails.schema";
import type { IncludedUsersOfProjectResponse, IncludedUserItem } from "@ServerTypes/accounts/accounts.schema";
import type { GetFormTemplateResponse } from "@ServerTypes/projects/project.schema";
import type { MailDataState, MailRecipient, FrontendAttachment, FrontendReference } from "./mailbox.interfaces";
import type { RootState, AppDispatch } from "@Reducers/index";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import type { SplitButtonOption } from "@CubeComponents/html/SplitButtonComponent";
import type {
  AutocompleteInputChangeReason,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";

interface CreateMailProps {
  projectId: string;
  refreshData: () => void;
  mail?: MailItem | null;
  onClose: () => void;
}

function CreateMail({ projectId, refreshData, mail, onClose }: CreateMailProps) {
  const { t } = useTranslation();
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const messageFooter = useMemo(
    () => "<br>" + sanitizeHtml(userDetails.messageFooterContent ?? ""),
    [userDetails.messageFooterContent],
  );
  const [mailData, setMailData] = useState<MailDataState>({
    sentTo: [],
    sentCC: [],
    subject: "",
    body: messageFooter,
    responseType: "",
    dueDate: null,
    attachments: [],
    references: [],
    tags: [],
  });
  const dispatch = useDispatch<AppDispatch>();
  const editorRef = useRef<{ destroy: () => void } | null>(null);
  const [formTemplate, setFormTemplate] = useState<FormTemplate | null>(null);
  const [isMailSending, setIsMailSending] = useState<boolean>(false);
  const [openAttachmentsModal, setOpenAttachmentsModal] = useState<boolean>(false);
  const [openReferencesModal, setOpenReferencesModal] = useState<boolean>(false);
  const [openAddUserModal, setOpenAddUserModal] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [mailingListType, setMailingListType] = useState<"TO" | "CC" | null>(null);
  const [excludedUsers, setExcludedUsers] = useState<string[]>([]);
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [openAddExternalUserModal, setOpenAddExternalUserModal] = useState<boolean>(false);
  const [openGridUserModal, setOpenGridUserModal] = useState<boolean>(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [showWarningMessage, setShowWarningMessage] = useState<boolean>(false);
  const [disableAddExternal, setDisableAddExternal] = useState<boolean>(true);
  const [projectUsers, setProjectUsers] = useState<{ users: IncludedUserItem[]; uniqueEmail: Set<string> }>({
    users: [],
    uniqueEmail: new Set(),
  });
  const [inherit, setInherit] = useState({
    showInheritButton: false,
    showInhertitModal: false,
    disabledInheritConfirmationModal: false,
    inheritVersionKey: 0,
  });
  const [externalUsersInProject, setExternalUsersInProject] = useState<string[]>([]);
  const [internalSelectedUsers, setInternalSelectedUsers] = useState<IncludedUserItem[]>([]);
  const ADD_USER_OPTIONS = [
    { id: "addUsers", optionDisplay: t("Add Users") },
    { id: "addExternal", optionDisplay: t("Add External Email ID") },
    { id: "selectFromGrid", optionDisplay: t("Select User(s) from Grid") },
  ];
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const formValidator = useFormValidator({});
  const projectData = useSelector((state: RootState) => state.projectData);
  const isMailForwarded = !!mail?.isMailForward;
  const parentId = isMailForwarded ? null : (mail?._id ?? null);

  const handleChange = (event: React.SyntheticEvent, usedEmails: string[]) => {
    const uniqueEmails = new Set<string>();
    let hasInvalidEmail = false;
    for (const email of usedEmails) {
      const cleanedEmail = email?.trim()?.toLowerCase();
      if (!cleanedEmail) continue;
      uniqueEmails.add(cleanedEmail);
      if (!getEmailValidity(cleanedEmail)) {
        hasInvalidEmail = true;
      }
    }
    if (!hasInvalidEmail) {
      handleProjectExternalUsers(usedEmails);
    }
    setEmails([...uniqueEmails]);
    setError(hasInvalidEmail);
    setDisableAddExternal(hasInvalidEmail || !uniqueEmails.size);
  };

  const getProjectUsers = useCallback(async () => {
    const query = { value: { projectId, source: "projectSetting" } };
    const response = await axiosWrapper.post<IncludedUsersOfProjectResponse>(
      `${getBackendUrl()}/accounts/includedUsersController`,
      query,
    );
    if (response.data.result) {
      const userEmails = response.data.result.map((user) => user.email).filter((e): e is string => !!e);
      setProjectUsers({
        users: response.data.result,
        uniqueEmail: new Set(userEmails),
      });
    }
  }, [projectId]);

  const getEmailValidity = (email: string) => emailRegex.test(email);

  useEffect(() => {
    handleProjectExternalUsers([]);
    setInherit((prev) => ({
      ...prev,
      showInheritButton: projectData.inheritMailCustomFields ?? false,
    }));
  }, [projectData]);

  const handleProjectExternalUsers = (externalUsedEmails: string[]) => {
    if (projectData.whitelistEmails) {
      const usedEmails: Array<string | undefined> = [...externalUsedEmails];
      if (mailingListType === "TO") {
        for (const user of mailData.sentTo) {
          usedEmails.push(user.email);
        }
      } else if (mailingListType === "CC") {
        for (const user of mailData.sentCC) {
          usedEmails.push(user.email);
        }
      }
      const usedSet = new Set(usedEmails);
      const remainingEmails = projectData.whitelistEmails
        .filter((email) => !usedSet.has(email))
        .sort((a, b) => a.localeCompare(b));
      setExternalUsersInProject(remainingEmails);
    }
  };

  useEffect(() => {
    if (!mail) return;
    if (isMailForwarded) {
      const body = sanitizeHtml(mail.body) + messageFooter;
      setMailData((prev) => ({
        ...prev,
        subject: mail.subject,
        body,
        attachments: mail.attachments,
        references: mail.references ?? [],
      }));
    } else {
      setMailData((prev) => ({
        ...prev,
        subject: mail.subject,
        sentTo: mail.sentTo,
        sentCC: mail.sentCC ?? [],
      }));
    }
  }, [mail, isMailForwarded, messageFooter]);

  useEffect(() => {
    const excludedUsersArray: string[] = [];
    for (const item of mailData.sentTo ?? []) {
      if (item._id && !excludedUsersArray.includes(item._id) && !item.isExternal) {
        excludedUsersArray.push(item._id);
      }
    }

    for (const item of mailData.sentCC ?? []) {
      if (item._id && !excludedUsersArray.includes(item._id) && !item.isExternal) {
        excludedUsersArray.push(item._id);
      }
    }

    setExcludedUsers(excludedUsersArray);
  }, [mailData.sentTo, mailData.sentCC]);

  const getFormTemplate = useCallback(async () => {
    const { data } = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplate`, {
      projectId,
      resourceType: formCategories.mailBox,
    });
    if (data?.data?.template) {
      setFormTemplate({
        template: data.data.template as FormTemplate["template"],
        _id: data.data._id,
      });
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      getProjectUsers();
      getFormTemplate();
    }
  }, [projectId, getProjectUsers, getFormTemplate]);

  const handleAddUsers = () => {
    setOpenAddUserModal(true);
  };

  const removeUserFromList = (userEmail: string, type: string, callFromAddExternal?: boolean) => {
    let hasExternal = false;
    if (callFromAddExternal) {
      const users = [...emails];
      const usersList = users.filter((user) => user !== userEmail);
      const hasInvalidEmail = usersList.some((email) => !getEmailValidity(email));

      if (hasInvalidEmail === error) {
        setExternalUsersInProject([...externalUsersInProject, userEmail].sort((a, b) => a.localeCompare(b)));
      } else {
        setError(hasInvalidEmail);
      }
      setDisableAddExternal(!(usersList.length && !hasInvalidEmail));
      setEmails(usersList);
    } else {
      if (type === "TO") {
        const users = [...mailData.sentTo];
        const usersList = users.filter((user) => user.email !== userEmail);
        const allUsers = [...usersList, ...mailData.sentCC];
        hasExternal = allUsers.some((user) => user.isExternal);
        setMailData((prev) => ({ ...prev, sentTo: usersList }));
      } else if (type === "CC") {
        const users = [...mailData.sentCC];
        const usersList = users.filter((user) => user.email !== userEmail);
        const allUsers = [...usersList, ...mailData.sentTo];
        hasExternal = allUsers.some((user) => user.isExternal);
        setMailData((prev) => ({ ...prev, sentCC: usersList }));
      }
      setShowWarningMessage(hasExternal);
    }
  };

  const saveAttachments = (files: FrontendAttachment[]) => {
    setMailData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }));
  };

  const handlewhitelistedUsers = async (users: Recipient[]) => {
    const mailIds: string[] = [];
    for (const user of users) {
      if (user.isExternal && user.email) {
        mailIds.push(user.email);
      }
    }
    if (mailIds.length) {
      const response = await addProjectWhitelistEmailsRequest({
        emails: mailIds,
        projectId,
      });
      if (response.data.status) {
        // `users` (the updated whitelist) is returned by the endpoint at runtime but is
        // not part of the typed AddProjectWhitelistEmailsResponse schema, so narrow-cast it.
        const whitelistEmails = (response.data as { users?: string[] }).users;
        dispatch(setProjectData({ ...projectData, whitelistEmails }));
      }
    }
  };

  const showToast = (message: string, status: boolean) => {
    dispatch(
      showToastNotification({
        content: untranslated(message),
        type: status ? TOAST_TYPE_SUCCESS : TOAST_TYPE_ERROR,
      }),
    );
  };

  const sendMail = async (attachments: Attachment[]) => {
    const users = [...mailData.sentTo, ...mailData.sentCC];
    // Transform references - keep fileVersion and spaceId for display/navigation
    const references: Reference[] = mailData.references.map((reference) => ({
      id: reference.id || reference._id,
      fileVersion: reference.fileVersion,
      spaceId: reference.spaceId,
      name: reference.name,
    }));
    const response = await sendMailRequest({
      ...mailData,
      references,
      parentId,
      projectId,
      formTemplate,
      attachments,
    });
    if (response.data.status) {
      refreshData();
      showToast(t("Mail sent Successfully."), true);
      dispatch(setProjectData({ ...projectData, mailsCounter: (projectData.mailsCounter ?? 0) + 1 }));
      handlewhitelistedUsers(users);
    } else {
      // The send endpoint returns an `errorMessage` on failure at runtime; the typed
      // SendMailResponse only declares `status`/`message`, so narrow-cast to read it.
      showToast((response.data as { errorMessage?: string }).errorMessage ?? "", false);
    }
    handleClose();
  };

  const handleEmailSend = async () => {
    if (formValidator.validateAll?.()) {
      setIsMailSending(true);
      if (mailData.attachments.length) {
        const newFiles = mailData.attachments.filter((item) => !item.awsKey && item.file);
        const existingFiles: Attachment[] = mailData.attachments
          .filter((item) => item.awsKey)
          .map((item) => ({
            attachmentsData: item.attachmentsData,
            key: item.awsKey!,
          }));
        let savedAttachments: Attachment[] = [];
        if (newFiles.length) {
          const uploadResults = await Promise.all(
            newFiles.filter((item) => item.file).map((item) => uploadStringToTemp(item.file!, item.file!.name)),
          );
          if (uploadResults.some((key) => !key)) {
            showToast(t("Failed to upload attachments"), false);
            handleClose();
            return;
          }
          savedAttachments = newFiles.map((item, ind: number) => ({
            attachmentsData: item.attachmentsData,
            key: uploadResults[ind]!,
            isNew: true,
          }));
        }
        sendMail([...savedAttachments, ...existingFiles]);
      } else {
        sendMail([]);
      }
    }
  };

  const handleClose = () => {
    setIsMailSending(false);
    if (editorRef.current) editorRef.current.destroy();
    onClose();
  };

  const mergeReferences = (merged: FrontendReference[]) => {
    return merged.filter((item, index, self) => index === self.findIndex((t) => t._id === item._id));
  };

  const handleFileSelectedButtonClick = (params: Record<string, FrontendReference>) => {
    const listOfFiles = Object.keys(params).map((fileData) => params[fileData]!);
    setMailData((prev) => ({
      ...prev,
      references: mergeReferences([...prev.references, ...listOfFiles]),
    }));
    setOpenReferencesModal(false);
  };

  const handleUserModalNext = (selectedUsers: Recipient[]) => {
    if (mailingListType === "TO") {
      setMailData((prev) => ({
        ...prev,
        sentTo: [...prev.sentTo, ...selectedUsers],
      }));
    } else if (mailingListType === "CC") {
      setMailData((prev) => ({
        ...prev,
        sentCC: [...prev.sentCC, ...selectedUsers],
      }));
    }
  };

  const handleCustomFieldsData = (data: FormTemplate["template"]) => {
    setFormTemplate((prev) => (prev ? { ...prev, template: data } : prev));
  };

  const formatUserTag = (
    data: MailRecipient,
    index: number,
    usersType: "TO" | "CC",
    valid?: boolean,
    callFromAddExternal?: boolean,
  ) => {
    const isExternal = data.isExternal || !data._id;
    const handleDeleteIconClicked = () => removeUserFromList(data.email ?? "", usersType, callFromAddExternal);
    return (
      <UserNameTagComponent
        id={data._id}
        fullName={data.email}
        isExternal={isExternal}
        handleDeleteIconClicked={handleDeleteIconClicked}
        chipBackground={getChipBackground(isExternal, valid)}
        chipBorder={getChipBorder(isExternal, valid)}
      />
    );
  };

  const getChipBackground = (isExternal: boolean, valid?: boolean): string | undefined => {
    if (isExternal) return valid ? "#fff" : "#FFE699";
    return undefined;
  };

  const getChipBorder = (isExternal: boolean, valid?: boolean): string | undefined => {
    if (isExternal) return valid ? "1px solid red" : "1px solid #FFCF55";
    return undefined;
  };

  const handleAddGridUsers = () => {
    setOpenGridUserModal(true);
  };

  const handleGridUserNext = (
    selectedUsers: Array<{ _id?: string; id?: string; email?: string; fullName?: string }>,
  ) => {
    const mappedUsers: Recipient[] = selectedUsers.map((user) => ({
      _id: user._id || user.id,
      email: user.email,
      fullName: user.fullName,
    }));
    const allUsers = [...mappedUsers, ...mailData.sentTo, ...mailData.sentCC];
    const hasExternal = allUsers.some((user) => user.isExternal);
    setShowWarningMessage(hasExternal);
    handleUserModalNext(mappedUsers);
  };

  const handleSplitButtonAction = (
    _event: React.MouseEvent<HTMLElement>,
    option: SplitButtonOption,
    type: "TO" | "CC",
  ) => {
    setMailingListType(type);
    const optionId = typeof option === "string" ? option : option.id;
    if (optionId === "addUsers") {
      handleAddUsers();
    } else if (optionId === "addExternal") {
      handleExternalUserModal();
    } else if (optionId === "selectFromGrid") {
      handleAddGridUsers();
    }
  };

  const sentToChips = useMemo(
    () => (
      <div className="flex-1">
        <div className="flex flex-wrap items-center justify-start gap-2">
          {mailData.sentTo.map((item, index) => formatUserTag(item, index, "TO"))}
        </div>
      </div>
    ),

    [mailData.sentTo, mailData.sentCC],
  );

  const sentCCChips = useMemo(
    () => (
      <div className="flex-1">
        <div className="flex flex-wrap items-center justify-start gap-2">
          {mailData.sentCC.map((item, index) => formatUserTag(item, index, "CC"))}
        </div>
      </div>
    ),

    [mailData.sentTo, mailData.sentCC],
  );

  const warningTemplate = () => (
    <div className="flex gap-1 bg-[#FFE699] -mt-6 -me-6 -ms-6 pt-2 pb-2 ps-4 pe-4">
      <span className="items-center">
        <IconComponent name="details" width="1.5rem" height="1.5rem" />
      </span>
      <Text>
        {t(
          "Be cautious about sharing sensitive information. This correspondence is being shared outside of your portal to user(s) not part of CUBE.",
        )}
      </Text>
    </div>
  );

  const toggleInheritModal = () => {
    setInherit((prev) => ({
      ...prev,
      showInhertitModal: !inherit.showInhertitModal,
      disabledInheritConfirmationModal: false,
    }));
  };

  const modelContent = () => (
    <div>
      {showWarningMessage && warningTemplate()}
      {parentId && mail && (
        <div className="flex justify-between">
          <div className="flex font-semibold my-4">
            {mail.mailNumber && <Text>{`${t("Replying to #")} ${mail.mailNumber}`}</Text>}
          </div>
          {formTemplate && inherit.showInheritButton && (
            <div className="flex justify-end">
              <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} disabled={false} onClick={toggleInheritModal}>
                <div className="flex gap-2">
                  <IconComponent name="Inherit" />
                  <Text className="font-semibold">{t("Inherit")}</Text>
                </div>
              </ButtonComponent>
            </div>
          )}
        </div>
      )}
      <div>
        <div className="flex items-start my-5 w-full gap-4">
          <Text className="flex-0 whitespace-nowrap me-2 w-24" data-testid="sendMailToText">
            {t("To")}
            <sup className="text-red-500 text-lg" data-testid="mandatoryIcon">
              *
            </sup>
          </Text>
          <SplitButton
            options={ADD_USER_OPTIONS}
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={(_e, option) => handleSplitButtonAction(_e, option, "TO")}
            onChange={(_e, option) => handleSplitButtonAction(_e, option, "TO")}
            buttonLabel="+"
            data-testid="plusIconToAddRecipient"
          />
          {sentToChips}
        </div>
        <div className="flex items-start my-5 w-full gap-4">
          <Text className="flex-0 whitespace-nowrap me-2 w-24" data-testid="addMailForCC">
            {t("CC")}
          </Text>
          <SplitButton
            options={ADD_USER_OPTIONS}
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={(_e, option) => handleSplitButtonAction(_e, option, "CC")}
            onChange={(_e, option) => handleSplitButtonAction(_e, option, "CC")}
            buttonLabel="+"
            data-testid="plusIconForCC"
          />
          {sentCCChips}
        </div>
        <div className="flex flex-1 items-center my-5 w-full gap-2">
          <Text className="flex-0 whitespace-nowrap w-28" data-testid="mailSubject">
            {t("Subject")}
            <sup className="text-red-500 text-lg">&nbsp;*</sup>
          </Text>
          <div className="flex-1">
            <TextFieldComponent
              disabled={!!parentId || isMailForwarded}
              value={mailData.subject}
              onChange={(e) => setMailData((prev) => ({ ...prev, subject: e.target.value }))}
              placeholder={t("Enter Subject")}
              data-testid="enterSubjectOfMail"
            />
          </div>
        </div>

        <MailContent
          mailData={mailData}
          setMailData={setMailData}
          setOpenAttachmentsModal={setOpenAttachmentsModal}
          setOpenReferencesModal={setOpenReferencesModal}
        />
        {formTemplate && (
          <div className="my-4">
            <FormPreview
              key={inherit.inheritVersionKey}
              formData={formTemplate}
              disabled={false}
              fullWidthInput
              templateSource={TEMPLATE_SOURCE_MAIL_CREATION}
              resourceType={formCategories.mailBox}
              handleData={handleCustomFieldsData}
              formValidator={formValidator}
            />
          </div>
        )}
      </div>
    </div>
  );

  const addUserFooterContent = () => (
    <div className="flex gap-5">
      <ButtonComponent
        disabled={disableAddExternal}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleSaveExternalUser}
      >
        <Text>{t("Add")}</Text>
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleExternalUserModal}>
        <Text>{t("Discard")}</Text>
      </ButtonComponent>
    </div>
  );

  const footerContent = () => (
    <div className="flex gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleEmailSend}
        disabled={!mailData.sentTo?.length || mailData.subject?.trim().length == 0 || isMailSending}
        data-testid="sendMailFooterBtn"
      >
        <Text>{t("Send")}</Text>
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleClose} data-testid="discardMailFooterBtn">
        <Text>{t("Discard")}</Text>
      </ButtonComponent>
    </div>
  );

  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === "input") {
      if (newInputValue.endsWith(",")) {
        const email = newInputValue.slice(0, -1).trim();
        if (email) {
          handleChange(event, [...emails, email]);
        }
        setInputValue("");
      } else {
        setInputValue(newInputValue);
      }
    }
    if (["clear", "reset"].includes(reason)) {
      setInputValue("");
    }
  };

  const handleSaveExternalUser = () => {
    if (emails.length) {
      let filteredEmails: string[] = [];
      if (mailingListType == "TO") {
        const existingEmails = new Set(mailData.sentTo.map((user) => user.email));
        filteredEmails = emails.filter((email) => !existingEmails.has(email));
      } else {
        const existingEmails = new Set(mailData.sentCC.map((user) => user.email));
        filteredEmails = emails.filter((email) => !existingEmails.has(email));
      }
      const externalUsers: Recipient[] = [];
      for (const email of filteredEmails) {
        if (projectUsers.users.length && projectUsers.uniqueEmail.has(email)) {
          const user = projectUsers.users.filter((item) => item.email === email);
          externalUsers.push(...user);
        } else {
          externalUsers.push({ email, isExternal: true });
        }
      }

      const allUsers = [...externalUsers, ...mailData.sentTo, ...mailData.sentCC];
      const hasExternal = allUsers.some((user) => user.isExternal);
      setShowWarningMessage(hasExternal);
      handleUserModalNext(externalUsers);
    }
    setDisableAddExternal(true);
    setOpenAddExternalUserModal((prev) => !prev);
  };

  const externalUserModelContent = () => (
    <div className="flex-col flex-start gap-1 py-4 items-center">
      <div className="flex items-center gap-2 w-full">
        <Text className="pe-2 shrink-0">{t("To")}</Text>
        <div className="flex-1 min-w-0">
          <AutoComplete
            multiple
            freeSolo
            options={externalUsersInProject}
            value={emails}
            inputValue={inputValue}
            onChange={(event: React.SyntheticEvent, newValue: string[], reason: AutocompleteChangeReason) => {
              handleChange(event, newValue);
              if (["createOption", "selectOption"].includes(reason)) {
                setInputValue("");
              }
            }}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: string) => (
              <li {...props} className="flex items-center gap-3 p-2 cursor-pointer">
                <Avatar style={{ backgroundColor: "#FB923C", width: "2rem", height: "2rem" }}>
                  <IconComponent name="userProfileWhite" width="1.25rem" height="1.25rem" />
                </Avatar>
                {option}
              </li>
            )}
            onInputChange={handleInputChange}
            renderTags={(value: string[]) =>
              value.map((option: string, index: number) => (
                <div key={index} className="p-1">
                  {formatUserTag(
                    { email: option, isExternal: true },
                    index,
                    mailingListType!,
                    !getEmailValidity(option),
                    true,
                  )}
                </div>
              ))
            }
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextFieldComponent
                {...params}
                variant="outlined"
                placeholder={emails.length === 0 ? "Enter Email ID" : ""}
                error={error}
                height="2.625rem"
                onBlur={(e) => {
                  const email = e.target.value.trim();
                  if (email) {
                    handleChange(e, [...emails, email]);
                  }
                  setInputValue("");
                }}
              />
            )}
          />
        </div>
      </div>

      {error && (
        <div className="text-red-500 mt-4 flex items-center h-2">
          <Text className="flex ps-6 items-center gap-2">
            <IconComponent name="CloseCancelRed" width="1rem" height="1rem" />
            {t("One or more email addresses are not properly formatted.")}
          </Text>
        </div>
      )}
    </div>
  );

  const handleExternalUserModal = () => {
    handleProjectExternalUsers([]);
    setEmails([]);
    setError(false);
    setDisableAddExternal(true);
    setOpenAddExternalUserModal((prev) => !prev);
  };

  const internalUserOptions = useMemo(() => {
    return projectUsers.users.filter(
      (user) => !excludedUsers.includes(user._id) && !internalSelectedUsers.some((s) => s._id === user._id),
    );
  }, [projectUsers.users, excludedUsers, internalSelectedUsers]);

  const handleSaveInternalUser = () => {
    if (internalSelectedUsers.length) {
      const allUsers: Recipient[] = [...internalSelectedUsers, ...mailData.sentTo, ...mailData.sentCC];
      const hasExternal = allUsers.some((user) => user.isExternal);
      setShowWarningMessage(hasExternal);
      handleUserModalNext(internalSelectedUsers);
    }
    setInternalSelectedUsers([]);
    setOpenAddUserModal(false);
  };

  const handleCloseInternalUserModal = () => {
    setInternalSelectedUsers([]);
    setOpenAddUserModal(false);
  };

  const internalUserModalContent = () => (
    <div className="flex-col flex-start gap-1 py-4 items-center">
      <div className="flex items-center gap-2 w-full">
        <Text className="pe-2 shrink-0">{t("To")}</Text>
        <div className="flex-1 min-w-0">
          <AutoComplete
            multiple
            options={internalUserOptions}
            value={internalSelectedUsers}
            getOptionLabel={(option: IncludedUserItem) => option.fullName || option.email || ""}
            isOptionEqualToValue={(option: IncludedUserItem, value: IncludedUserItem) => option._id === value._id}
            onChange={(_event: React.SyntheticEvent, newValue: IncludedUserItem[]) => {
              setInternalSelectedUsers(newValue);
            }}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: IncludedUserItem) => (
              <li {...props} className="flex items-center gap-3 p-2 cursor-pointer">
                <Avatar style={{ backgroundColor: "var(--system-colour-2)", width: "2rem", height: "2rem" }}>
                  <span className="text-white text-xs">
                    {(option.fullName || "")
                      .split(" ")
                      .map((n: string) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                </Avatar>
                <div className="flex flex-col">
                  <span>{option.fullName}</span>
                  <span className="text-xs text-gray-500">{option.email}</span>
                </div>
              </li>
            )}
            renderTags={(value: IncludedUserItem[]) =>
              value.map((option: IncludedUserItem) => (
                <div key={option._id} className="p-1">
                  <UserNameTagComponent
                    id={option._id}
                    handleDeleteIconClicked={() => {
                      setInternalSelectedUsers((prev) => prev.filter((u) => u._id !== option._id));
                    }}
                  />
                </div>
              ))
            }
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextFieldComponent
                {...params}
                variant="outlined"
                placeholder={internalSelectedUsers.length === 0 ? t("Search Users") : ""}
                height="2.625rem"
              />
            )}
          />
        </div>
      </div>
    </div>
  );

  const internalUserFooterContent = () => (
    <div className="flex gap-5">
      <ButtonComponent
        disabled={!internalSelectedUsers.length}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleSaveInternalUser}
      >
        <Text>{t("Add")}</Text>
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleCloseInternalUserModal}>
        <Text>{t("Discard")}</Text>
      </ButtonComponent>
    </div>
  );

  const handleAddExternalUserModal = () => (
    <ModalStateDialogComponent
      isVisible
      headerTitle={t("Add External Email ID")}
      onCancel={handleExternalUserModal}
      width="50%"
      modelContent={externalUserModelContent()}
      footerContent={addUserFooterContent()}
    />
  );

  const getHeaderTitle = () => {
    if (isMailForwarded) return t("Forward Mail");
    if (parentId) return t("Reply Mail");
    return t("New Mail");
  };

  const handleInhertCustomFields = async () => {
    setInherit((prev) => ({
      ...prev,
      disabledInheritConfirmationModal: true,
    }));
    if (!parentId) {
      toggleInheritModal();
      return;
    }
    const response = await inheritMailCustomFields({ parentId, projectId });
    if (response.data.status) {
      const updatedTemplate = response.data?.data?.updatedTemplate;
      if (updatedTemplate?.length) {
        handleCustomFieldsData(updatedTemplate);
      }
      setInherit((prev) => ({
        ...prev,
        inheritVersionKey: inherit.inheritVersionKey + 1,
        showInheritButton: false,
      }));
      dispatch(
        showToastNotification({
          content: t("Custom Fields successfully inherited from # {{mailNumber}}", { mailNumber: mail?.mailNumber }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(response.data.errorMessage || ""),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    toggleInheritModal();
  };

  const inheritCustomFieldsFooter = () => (
    <div className="flex gap-5">
      <ButtonComponent
        disabled={inherit.disabledInheritConfirmationModal}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleInhertCustomFields}
      >
        <Text>{t("Confirm")}</Text>
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={toggleInheritModal}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const inheritCustomFieldsContent = () => (
    <div className="flex flex-col">
      <Text>
        {t("Are you sure you want to inherit Custom Field values from # {{mailNumber}}?", {
          mailNumber: mail?.mailNumber,
        })}
      </Text>
      <Text>{t("This action may override any existing Custom Field values that were added.")}</Text>
    </div>
  );

  const inheritCustomFieldsModal = () => (
    <ModalStateDialogComponent
      isVisible
      headerTitle={t("Inherit")}
      onCancel={toggleInheritModal}
      width="30%"
      modelContent={inheritCustomFieldsContent()}
      footerContent={inheritCustomFieldsFooter()}
    />
  );

  return (
    <>
      {openAttachmentsModal && (
        <AttachmentsModal saveAttachments={saveAttachments} onClose={() => setOpenAttachmentsModal(false)} />
      )}
      {openReferencesModal && (
        <FileManagerAsPopup
          handleFileSelectedButtonClick={handleFileSelectedButtonClick}
          handleFileSelectedCancelClick={() => setOpenReferencesModal(false)}
          id="fileManagerAsPopup"
          workspaceId={spaceId}
          hideIssues
          hideTasks
          hideSchedules
          hideEstimates
          hideExternalSpace
          hideReviews
          heading={t("Add References")}
          disableFiles={[]}
          projectId={projectId}
          setSpaceId={setSpaceId}
          allowSwitchSpace
        />
      )}
      {openAddUserModal && (
        <ModalStateDialogComponent
          isVisible
          headerTitle={t("Add Users")}
          onCancel={handleCloseInternalUserModal}
          width="50%"
          modelContent={internalUserModalContent()}
          footerContent={internalUserFooterContent()}
        />
      )}
      {openAddExternalUserModal && handleAddExternalUserModal()}
      {openGridUserModal && (
        <AddUsersToMailList
          closeModal={() => setOpenGridUserModal(false)}
          projectId={projectId}
          excludedUsers={excludedUsers}
          handleNext={handleGridUserNext}
        />
      )}
      {inherit.showInhertitModal && inheritCustomFieldsModal()}
      <ModalStateDialogComponent
        isVisible
        headerTitle={getHeaderTitle()}
        onCancel={onClose}
        width="60%"
        modelContent={modelContent()}
        footerContent={footerContent()}
      />
    </>
  );
}

export default CreateMail;
